import React, { useEffect, useState } from "react";
import { Smartphone, Share, X, Plus } from "lucide-react";
import { isStandalone, isIos, markInstalled } from "@/lib/installPwa";

// Detect if we can install at all:
//  - Android/Chromium  → `beforeinstallprompt` (captured in index.html)
//  - iOS Safari        → show a short manual guide
//  - Anything else (desktop Safari/Firefox) → hide the banner entirely.
const canShowIosGuide = () => isIos();

export default function InstallPwaPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(
    typeof window !== "undefined" ? window.__ffDeferredPrompt : null
  );
  const [standalone, setStandalone] = useState(isStandalone());
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    if (standalone) return;

    const onReady = () => setDeferredPrompt(window.__ffDeferredPrompt || null);
    const onBeforeInstall = (e) => {
      e.preventDefault();
      window.__ffDeferredPrompt = e;
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      markInstalled();
      setStandalone(true);
      setDeferredPrompt(null);
      window.__ffDeferredPrompt = null;
    };

    window.addEventListener("famfeel:install-ready", onReady);
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // In case the event fired *after* this component mounted but before
    // we attached the listener above, re-check the global.
    if (!deferredPrompt && window.__ffDeferredPrompt) {
      setDeferredPrompt(window.__ffDeferredPrompt);
    }

    return () => {
      window.removeEventListener("famfeel:install-ready", onReady);
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standalone]);

  if (standalone) return null;

  const iosSupported = canShowIosGuide();
  const canNativePrompt = !!deferredPrompt;

  // If neither a native prompt nor the iOS path is available (e.g. desktop
  // Firefox / desktop Safari), don't show the banner at all — nothing useful
  // to offer.
  if (!canNativePrompt && !iosSupported) return null;

  const handleClick = async () => {
    if (canNativePrompt) {
      try {
        await deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice && choice.outcome === "accepted") {
          markInstalled();
          setStandalone(true);
        }
      } catch { /* noop */ }
      setDeferredPrompt(null);
      window.__ffDeferredPrompt = null;
      return;
    }
    setShowIosSheet(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="w-full mb-4 md:mb-6 flex items-center justify-between gap-3 rounded-[18px] border border-[color:var(--line)] bg-[color:var(--bg-soft)] hover:bg-[color:var(--surface)] transition-colors px-4 py-3 text-left"
        data-testid="install-pwa-prompt"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: "var(--ink)", color: "var(--bg)" }}
          >
            <Smartphone className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-[15px] leading-tight truncate">
              {canNativePrompt ? "Add FamFeel to your home screen" : "Add FamFeel to your home screen"}
            </div>
            <div className="text-[13px] text-[color:var(--ink-2)] leading-tight mt-0.5">
              {canNativePrompt
                ? "One tap — everyone opens the app with the Ff icon."
                : "So everyone taps in with one tap — no bookmarks, no sign-in."}
            </div>
          </div>
        </div>
        <span className="label-tiny text-[color:var(--ink-2)] whitespace-nowrap">
          {canNativePrompt ? "Install →" : "How? →"}
        </span>
      </button>

      {showIosSheet && (
        <IosInstructionsSheet
          onClose={() => setShowIosSheet(false)}
          onConfirmInstalled={() => {
            markInstalled();
            setStandalone(true);
            setShowIosSheet(false);
          }}
        />
      )}
    </>
  );
}

function IosInstructionsSheet({ onClose, onConfirmInstalled }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="install-pwa-ios-sheet"
    >
      <div
        className="relative bg-[color:var(--bg)] rounded-t-[28px] md:rounded-[20px] w-full md:max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <div className="label-tiny mb-1">Add to home screen</div>
            <h3 className="font-display text-2xl leading-tight">Two quick taps.</h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
            data-testid="install-pwa-ios-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <ol className="space-y-5 text-[16px] leading-snug text-[color:var(--ink-2)]">
          <li className="flex items-center gap-4">
            <Share className="w-7 h-7 flex-shrink-0 text-[color:var(--ink)]" strokeWidth={1.5} />
            <span>
              Tap the <strong className="text-[color:var(--ink)]">Share</strong> icon at the bottom of Safari.
            </span>
          </li>
          <li className="flex items-center gap-4">
            <Plus className="w-7 h-7 flex-shrink-0 text-[color:var(--ink)]" strokeWidth={1.5} />
            <span>
              Scroll down and tap <strong className="text-[color:var(--ink)]">Add to Home Screen</strong>.
            </span>
          </li>
        </ol>

        <div className="mt-7 flex flex-wrap gap-2 justify-end">
          <button onClick={onClose} className="ff-btn" data-testid="install-pwa-ios-later">
            Later
          </button>
          <button
            onClick={onConfirmInstalled}
            className="ff-btn ff-btn-primary"
            data-testid="install-pwa-ios-done"
          >
            I added it
          </button>
        </div>
      </div>
    </div>
  );
}
